import { Autocomplete, Box, Grid, styled } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'
/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch, toBuddhistYear } from 'utils/app.utils'

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import ExportToExcel from './ExportToExcel'
import LabReportList from './LabReportList'

/**STYLE */
import DoctorScheduleApi from 'api/counter/doctor-schedule.api'
import LabCompanyApi from 'api/setting/default-labs/lab-company.api'
import LabGroupsApi from 'api/setting/default-labs/lab-group.api'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import AutocompleteSelect from 'component/Select/AutocompleteSelect'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

const AutoCompleteForm = styled(Autocomplete)(({ theme }) => ({ '& input.MuiOutlinedInput-input': { height: '30px' } }))

export default function LabReport() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNowRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange
  const momentNow = moment().format('YYYY-MM-DD')

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [labDate, setlabDate] = useState<string>(momentNow)

  const [doctorsMultiple, setDoctorsMultiple] = useState<DentistInterface[]>([])
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [selectDoctors, setSelectDoctors] = useState<any[]>(['ALL'])

  const [labGroup, setLabGroup] = useState<any[]>([])
  const [labCompany, setLabCompany] = useState<any[]>([])
  const [labGroupId, setLabGroupId] = useState<number>(0)
  const [labCompanyId, setLabCompanyId] = useState<number>(0)
  const [selectedLabCompany, setSelectedLabCompany] = useState({ labCompanyId: 0, labCompanyName: t('REPORT.REPORT_GROUP.ALL') })
  const [selectedLabGroup, setSelectedLabGroup] = useState({ labGroupId: 0, labGroupName: t('REPORT.REPORT_GROUP.ALL') })

  const [labData, setLabData]: any = useState<any[]>([])
  const [labPage, setLabPage] = useState<number>(1)
  const [labPageLimit, setLabPageLimit] = useState<number>(10)
  const [openFilterDoctors, setOpenFilterDoctors] = useState(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [fetch, setFetch] = useState<boolean>(false)

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'LAB_ORDER') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    return true
  }

  const loadData = useCallback(async () => {
    if (_.isEmpty(branches))
      ClinicApi.findAllBranches()
        .then(({ data }) => setBranches(data))
        .catch((e) => {
          return
        })

    if (_.isEmpty(doctors))
      ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1])
        .then((resp) => setDoctors(resp.data))
        .catch((e) => {
          return
        })
  }, [])

  const loadDoctor = useCallback(async () => {
    DoctorScheduleApi.findAllDoctor({ branchId: branchId }).then(({ data: dataAllDoctor }) => {
      const dentists: DentistInterface[] = dataAllDoctor.map((d: any) => {
        return { dentistId: d.userId, dentistFullname: d.fullname }
      })
      setDoctorsMultiple(dentists)
      setSelectDoctors(['ALL', ...dentists.map((d: DentistInterface) => d.dentistId)])
    })
  }, [branchId])

  const loadLabGroup = useCallback((search = '') => {
    LabGroupsApi.findAll({ page: 1, pageLimit: 50, status: 'ACTIVE', search: search })
      .then(({ data: dataLabGroup }) => {
        setLabGroup(dataLabGroup)
      })
      .catch((e) => {
        return
      })
  }, [])

  const loadLabCompany = useCallback((search = '') => {
    LabCompanyApi.findAll({ page: 1, pageLimit: 50, status: 'ACTIVE', search: search })
      .then(({ data: dataLabCompany }) => {
        setLabCompany(dataLabCompany)
      })
      .catch((e) => {
        return
      })
  }, [])

  const fetchLab = useCallback(async () => {
    await verifyPermission()

    let condition: FindAllReportInterface = {
      page: labPage,
      pageLimit: labPageLimit,
      branchId: branchId
    }
    if (doctors) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: _.filter(selectDoctors, (v: any) => v > 0).join(',') }
    }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    if (rangeDate) {
      if (rangeDate[0] !== null && rangeDate[1] !== null) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
    }
    if (labCompanyId) condition = { ...condition, labCompanyId: labCompanyId }
    if (labGroupId) condition = { ...condition, labGroupId: labGroupId }

    ReportApi.reportLab(condition)
      .then(({ headers, data }) => {
        setLabData(data)
        setRowCount(headers['x-total'])
      })
      .catch((e) => setLoading(false))
      .finally(() => setLoading(false))

    setFetch(false)
  }, [labPage, labPageLimit, sortBy, sortType, fetch])

  const setDoc = (data: any) => {
    const sortedSelectData = data.sort((a: any, b: any) => {
      const idA = a.dentistId
      const idB = b.dentistId
      return selectDoctors.indexOf(idA) - selectDoctors.indexOf(idB)
    })
    return sortedSelectData
  }
  const filter = [
    branchId === 0 ? `${t(`REPORT.FILTER.ALL`)}` : `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`,
    rangeDate[0],
    rangeDate[1],
    _.isEmpty(selectDoctors)
      ? ` ${t('REPORT.FILTER.NO_SELECT')}`
      : selectDoctors.filter((i) => typeof i === 'number').length === doctors.length
        ? `${t('REPORT.FILTER.ALL')}`
        : `${setDoc(doctors.filter((d: any) => selectDoctors.includes(d.userId)))
          ?.map((d: any) => d.fullname)
          .slice(0, 3) // เเสดงเเค่ 3 คนเเรก
          .join(', ')}`,
    labGroupId ? `${_.get(_.find(labGroup, { labGroupId: labGroupId }), 'labGroupName') || _.get(_.find(labGroup, { labGroupId: labGroupId }), 'labGroupNameEn')} ` : `${t(`REPORT.FILTER.ALL`)}`,
    labCompanyId ? `${_.get(_.find(labCompany, { labCompanyId: labCompanyId }), 'labCompanyName') || _.get(_.find(labCompany, { labCompanyId: labCompanyId }), 'labCompanyNameEn')} ` : `${t(`REPORT.FILTER.ALL`)}`
  ]

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.LAB`)}${rangeDate[0] ? ' (' + rangeDate + ')' : ''}`,
    sheetName: `${t(`REPORT.REPORT_NAME.LAB`)}`
  })

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => setLabPageLimit(limit)
  const handleChangePage = (val: number) => setLabPage(val)

  const handleFilterDoctors = (value: any) => {
    if ((value === 'ALL' && _.size(selectDoctors.filter((item) => typeof item === 'number')) === _.size(doctors)) || value === 'CLEAR') setSelectDoctors([''])
    else if (value === 'ALL') setSelectDoctors([..._.map(doctors, (d: any) => d.userId)])
    else if (_.includes(selectDoctors, value)) setSelectDoctors(_.filter(selectDoctors, (d) => d !== value))
    else setSelectDoctors([...selectDoctors, value])
  }

  const exportExcel = async () => {
    try {
      setLoading(true)

      // Construct filter data for export
      const exportFilter = [
        branchId === 0 ? `${t(`REPORT.FILTER.ALL`)}` : `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`,
        toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY'),
        toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY'),
        _.isEmpty(selectDoctors)
          ? ` ${t('REPORT.FILTER.NO_SELECT')}`
          : selectDoctors.filter((i) => typeof i === 'number').length === doctors.length
            ? `${t('REPORT.FILTER.ALL')}`
            : `${setDoc(doctors.filter((d: any) => selectDoctors.includes(d.userId)))
              ?.map((d: any) => d.fullname)
              .slice(0, 3)
              .join(', ')}`,
        labGroupId ? `${_.get(_.find(labGroup, { labGroupId: labGroupId }), 'labGroupName') || _.get(_.find(labGroup, { labGroupId: labGroupId }), 'labGroupNameEn')} ` : `${t(`REPORT.FILTER.ALL`)}`,
        labCompanyId ? `${_.get(_.find(labCompany, { labCompanyId: labCompanyId }), 'labCompanyName') || _.get(_.find(labCompany, { labCompanyId: labCompanyId }), 'labCompanyNameEn')} ` : `${t(`REPORT.FILTER.ALL`)}`
      ]

      await ExportToExcel({
        data: labData,
        branches,
        branchId,
        filter: exportFilter,
        filterDateStart: rangeDate[0],
        filterDateEnd: rangeDate[1],
        t
      })
    } catch (error) {
      console.error('Export error:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    loadLabGroup()
    loadLabCompany()
  }, [loadData, loadLabGroup, loadLabCompany])
  useEffect(() => {
    loadDoctor()
  }, [loadDoctor])

  useEffect(() => {
    fetchLab()
  }, [fetchLab])

  return (
    <>
      <Box >
        <HeaderCard text={t(`REPORT.REPORT_NAME.LAB`)} />
        <Box sx={{ px: 3, pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <SelectInput
                label={t('REPORT.FILTER.BRANCH')}
                value={branchId}
                onChange={e => {
                  setBranchId(Number(e.target.value));
                  setLabPage(1)
                }}
                options={branches.map(b => ({
                  value: b.branchId,
                  name: b.branchName || b.branchNameEn || ''
                }))}
                width="100%"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} xl={2}>
              <InputSecondNewRangePicker
                inputHeight={40}
                value={rangeDate || emptyRangePicker}
                onchange={(val: any) => {
                  if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                  else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                  setLabPage(1)
                }}
                label={''}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <SelectCheckBoxInput
                label={t('USER_DOCTOR')}
                options={doctors.map(d => ({
                  id: d.userId,
                  name: d.fullname
                }))}
                selected={selectDoctors}
                onChange={(ids: any) => {
                  setSelectDoctors(ids)
                  setOpenFilterDoctors(false)
                }}
                fullWidth
                width="100%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <AutocompleteSelect
                key={'lab-group'}
                labelId="lab-group"
                height={40}
                inputLabel={t('REPORT.FILTER.LAB_GROUP')}
                noOptionsText={t('REGISTER.FORM.NOT_FOUND')}
                options={[{ labGroupId: 0, labGroupName: t('REPORT.REPORT_GROUP.ALL') }, ...labGroup]}
                getOptionLabel={(option: any) => option.labGroupName}
                renderOption={(props, option: any) => (
                  <Box key={option} component="li" {...props}>
                    {option.labGroupName}
                  </Box>
                )}
                onchange={(e, value: any) => {
                  if (value) {
                    setLabGroupId(value.labGroupId)
                    setSelectedLabGroup({ labGroupId: value.labGroupId, labGroupName: value.labGroupName })
                  } else {
                    setLabGroupId(0)
                    setSelectedLabGroup({ labGroupId: 0, labGroupName: t('REPORT.REPORT_GROUP.ALL') })
                  }
                }}
                inputProps={{
                  onChange: (e: any) => {
                    if (labGroup.length === 50) {
                      setLabGroupId(0)
                      setLabGroup([])
                      loadLabGroup(e.target.value)
                    }
                  }
                }}
                onOpen={(e: any) => {
                  setLabGroupId(0)
                  setSelectedLabGroup({ labGroupId: 0, labGroupName: t('REPORT.REPORT_GROUP.ALL') })
                  loadLabGroup()
                }}
                value={selectedLabGroup}
                disableClearable
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <AutocompleteSelect
                key={'lab-company'}
                labelId="lab-company"
                height={40}
                inputLabel={t('REPORT.FILTER.LAB_COMPANY')}
                noOptionsText={t('REGISTER.FORM.NOT_FOUND')}
                options={[{ labCompanyId: 0, labCompanyName: t('REPORT.REPORT_GROUP.ALL') }, ...labCompany]}
                getOptionLabel={(option: any) => option.labCompanyName}
                renderOption={(props, option: any) => (
                  <Box key={option} component="li" {...props}>
                    {option.labCompanyName}
                  </Box>
                )}
                onchange={(e, value: any) => {
                  if (value) {
                    setLabCompanyId(value.labCompanyId)
                    setSelectedLabCompany({ labCompanyId: value.labCompanyId, labCompanyName: value.labCompanyName })
                  } else {
                    setLabCompanyId(0)
                    setSelectedLabCompany({ labCompanyId: 0, labCompanyName: t('REPORT.REPORT_GROUP.ALL') })
                  }
                }}
                inputProps={{
                  onChange: (e: any) => {
                    if (labCompany.length === 50) {
                      setLabCompanyId(0)
                      setLabCompany([])
                      loadLabCompany(e.target.value)
                    }
                  }
                }}
                onOpen={(e: any) => {
                  setLabCompanyId(0)
                  setSelectedLabCompany({ labCompanyId: 0, labCompanyName: t('REPORT.REPORT_GROUP.ALL') })
                  loadLabCompany()
                }}
                value={selectedLabCompany}
                disableClearable
              />
            </Grid>
            <Grid item xs={12} sm={6} md={9} xl={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <MainButton
                  title={t('INPUT.SEARCH')}
                  onClick={() => {
                    setFetch(true);
                    setLabPage(1);
                    setLoading(true);
                  }}
                  type={'primary'}
                  variant={'contained'}
                  style={{ width: '120px' }}
                />
                <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                  if (e === 'Excel') {
                    exportExcel();
                  } else if (e === 'PDF') {
                    const style = document.createElement("style");
                    style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                    document.head.appendChild(style);
                    window.print();
                    document.head.removeChild(style);

                  }
                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
              </Box>
            </Grid>
          </Grid>
          <Box marginTop="16px">
            <LabReportList
              date={rangeDate}
              page={labPage}
              pageLimit={labPageLimit}
              rowCount={rowCount}
              sortBy={sortBy}
              sortType={sortType}
              data={labData}
              onRequestSort={onRequestSort}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleChangePage={handleChangePage}
              componentRef={componentRef}
              filter={filter}
            />
          </Box>
        </Box>
      </Box>
    </>
  )
}
